/**
 * Cloud File API Services
 *
 * React Query hooks for cloud storage file browsing operations.
 */

import { useQuery, useMutation, useQueryClient } from "react-query";
import axios from "axios";
import { nuclearn_api_url } from "src/App";
import { isPermissionDeniedError } from "../utils/errorHandlers";
import { assertValidCloudPath } from "src/reusable/utils/cloudPathValidation";
import { formatFileSize } from "src/reusable/services/fileUploadManagerService";
import type {
  CloudProviderType,
  CloudProviderInfo,
  CloudTreeNode,
  CloudFileMetadata,
  CredentialVerificationResult,
} from "src/types/cloudFiles";

// =============================================================================
// API Functions
// =============================================================================

/**
 * List all registered cloud providers with display names and descriptions.
 */
const getCloudProviders = async (): Promise<CloudProviderInfo[]> => {
  const response = await axios.get(
    `${nuclearn_api_url}/document-sources/providers`,
  );
  return response.data;
};

/**
 * List external connections for a specific provider type.
 */
const getCloudConnections = async (
  providerType: CloudProviderType,
): Promise<
  Array<{
    id: number;
    name: string;
    create_date: string;
    create_user_fk: number;
    connection_type: string;
  }>
> => {
  const response = await axios.get(
    `${nuclearn_api_url}/document-sources/${providerType}/connections`,
  );
  return response.data;
};

/**
 * Verify credentials for an external connection.
 */
const verifyCredentials = async (
  providerType: CloudProviderType,
  connectionId: number,
): Promise<CredentialVerificationResult> => {
  const response = await axios.post(
    `${nuclearn_api_url}/document-sources/${providerType}/${connectionId}/verify-credentials`,
  );
  return response.data;
};

/**
 * Get directory tree from cloud storage.
 */
const getCloudTree = async (
  providerType: CloudProviderType,
  connectionId: number,
  path: string = "/",
  maxDepth: number = 3,
): Promise<CloudTreeNode[]> => {
  // Validate path before API call (defense-in-depth)
  assertValidCloudPath(path);

  const response = await axios.get(
    `${nuclearn_api_url}/document-sources/${providerType}/${connectionId}/tree`,
    {
      params: { path, max_depth: maxDepth },
    },
  );
  return response.data;
};

/**
 * Get metadata for a specific file or folder.
 */
const getCloudMetadata = async (
  providerType: CloudProviderType,
  connectionId: number,
  filePath: string,
): Promise<CloudFileMetadata> => {
  // Validate path before API call (defense-in-depth)
  assertValidCloudPath(filePath);
  const response = await axios.get(
    `${nuclearn_api_url}/document-sources/${providerType}/${connectionId}/metadata`,
    {
      params: { file_path: filePath },
    },
  );
  return response.data;
};

// =============================================================================
// Exported API Functions (for imperative use)
// =============================================================================

/**
 * Fetch subtree for a specific folder path.
 * Used for lazy loading when expanding folders beyond initial maxDepth.
 *
 * @param providerType - Cloud provider type
 * @param connectionId - External connection ID
 * @param path - Folder path to fetch children for
 * @param maxDepth - Maximum depth to fetch (default 1 for immediate children)
 * @returns Array of child nodes
 */
export const fetchCloudSubtree = async (
  providerType: CloudProviderType,
  connectionId: number,
  path: string,
  maxDepth: number = 1,
): Promise<CloudTreeNode[]> => {
  return getCloudTree(providerType, connectionId, path, maxDepth);
};

// =============================================================================
// React Query Hooks
// =============================================================================

/**
 * Hook to list available cloud providers.
 */
export const useCloudProviders = () => {
  return useQuery(["cloud-providers"], getCloudProviders, {
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to list connections for a specific cloud provider.
 */
export const useCloudConnections = (providerType: CloudProviderType | null) => {
  return useQuery(
    ["cloud-connections", providerType],
    () => getCloudConnections(providerType!),
    {
      enabled: !!providerType,
      staleTime: 30 * 1000, // 30 seconds
      refetchOnWindowFocus: false,
    },
  );
};

/**
 * Hook to verify credentials for a connection.
 * Returns a mutation that can be triggered manually.
 */
export const useVerifyCredentials = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      providerType,
      connectionId,
    }: {
      providerType: CloudProviderType;
      connectionId: number;
    }) => verifyCredentials(providerType, connectionId),
    {
      onSuccess: (_, variables) => {
        // Invalidate connections list to potentially update last verified timestamp
        queryClient.invalidateQueries([
          "cloud-connections",
          variables.providerType,
        ]);
      },
    },
  );
};

/**
 * Hook to get directory tree from cloud storage.
 * Includes special handling for 403 permission errors.
 */
export const useCloudTree = (
  providerType: CloudProviderType | null,
  connectionId: number | null,
  path: string = "/",
  maxDepth: number = 3,
) => {
  return useQuery(
    ["cloud-tree", providerType, connectionId, path, maxDepth],
    () => getCloudTree(providerType!, connectionId!, path, maxDepth),
    {
      enabled: !!providerType && !!connectionId,
      staleTime: 60 * 1000, // 1 minute
      refetchOnWindowFocus: false,
      // Don't retry 403 permission errors - they won't succeed
      retry: (failureCount, error) => {
        if (isPermissionDeniedError(error)) return false;
        return failureCount < 2;
      },
    },
  );
};

/**
 * Hook to get metadata for a specific file.
 */
export const useCloudMetadata = (
  providerType: CloudProviderType | null,
  connectionId: number | null,
  filePath: string | null,
) => {
  return useQuery(
    ["cloud-metadata", providerType, connectionId, filePath],
    () => getCloudMetadata(providerType!, connectionId!, filePath!),
    {
      enabled: !!providerType && !!connectionId && !!filePath,
      staleTime: 30 * 1000, // 30 seconds
      refetchOnWindowFocus: false,
    },
  );
};

/**
 * Hook to manually fetch a subtree (for lazy loading on expand).
 * Returns a mutation that can be triggered on node expansion.
 */
export const useFetchSubtree = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      providerType,
      connectionId,
      path,
      maxDepth = 1,
    }: {
      providerType: CloudProviderType;
      connectionId: number;
      path: string;
      maxDepth?: number;
    }) => getCloudTree(providerType, connectionId, path, maxDepth),
    {
      onSuccess: (data, variables) => {
        // Update the cache with the new subtree data
        queryClient.setQueryData(
          [
            "cloud-tree",
            variables.providerType,
            variables.connectionId,
            variables.path,
            variables.maxDepth,
          ],
          data,
        );
      },
    },
  );
};

// =============================================================================
// Utility Functions
// =============================================================================

// formatFileSize is re-exported from the shared utility for consumers that import from this module
export { formatFileSize };

/**
 * Format date for display.
 */
export const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateString;
  }
};

/**
 * Get file extension from filename.
 */
export const getFileExtension = (filename: string): string => {
  const lastDot = filename.lastIndexOf(".");
  if (lastDot === -1 || lastDot === 0) return "";
  return filename.substring(lastDot).toLowerCase();
};

/**
 * Get icon name based on file type.
 */
export const getFileIcon = (metadata: CloudFileMetadata): string => {
  if (metadata.is_directory) {
    return "folder";
  }

  const ext = getFileExtension(metadata.name);
  const iconMap: Record<string, string> = {
    ".pdf": "picture_as_pdf",
    ".doc": "description",
    ".docx": "description",
    ".xls": "table_chart",
    ".xlsx": "table_chart",
    ".ppt": "slideshow",
    ".pptx": "slideshow",
    ".txt": "article",
    ".csv": "table_chart",
    ".json": "data_object",
    ".xml": "code",
    ".zip": "folder_zip",
    ".rar": "folder_zip",
    ".7z": "folder_zip",
    ".jpg": "image",
    ".jpeg": "image",
    ".png": "image",
    ".gif": "image",
    ".svg": "image",
    ".mp4": "video_file",
    ".avi": "video_file",
    ".mov": "video_file",
    ".mp3": "audio_file",
    ".wav": "audio_file",
  };

  return iconMap[ext] || "insert_drive_file";
};
